// Utility for bridging web <-> native (ReactNativeWebView)
// Use for share, download, etc. Fallbacks to browser if not in WebView.

import { GeoLocation } from '@/types/nativeBridge.types';
import { CalendarEventInput } from '@/utils/calendarExport';
import { CALENDAR_ALERT_MINUTES_BEFORE } from '@/constants/timings';

/**
 * Check if running in React Native WebView
 */
export function isNativeWebView(): boolean {
  return !!(globalThis.window && (globalThis.window as any).ReactNativeWebView);
}

/**
 * Send message to React Native
 */
function postNativeMessage(message: { type: string; payload?: any }): void {
  if (isNativeWebView()) {
    (globalThis.window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
  } else {
    console.warn('[NativeBridge] Not running in React Native WebView');
  }
}

// Trigger native camera for avatar/profile photo
export function triggerNativeCamera(): void {
  postNativeMessage({ type: 'open_camera' });
}

// Trigger native gallery for avatar/profile photo
export function triggerNativeGallery(): void {
  postNativeMessage({ type: 'open_gallery' });
}

/**
 * Start native location tracking for a target location
 * @param targetLocation - The golf course location to track distance from
 * @param distanceThresholdYards - Optional distance threshold in yards (defaults to 50 on native side)
 */
export function startLocationTracking(targetLocation: GeoLocation, distanceThresholdYards?: number): void {
  console.log('[NativeBridge] Starting location tracking for:', targetLocation, 'with threshold:', distanceThresholdYards);
  postNativeMessage({
    type: 'start_location_tracking',
    payload: { 
      geoLocation: targetLocation,
      distanceThresholdYards 
    }
  });
}

/**
 * Stop native location tracking
 */
export function stopLocationTracking(): void {
  console.log('[NativeBridge] Stopping location tracking');
  postNativeMessage({ type: 'stop_location_tracking' });
}

/** Which way the native status bar draws its glyphs — see below. */
export type NativeStatusBarStyle = 'light' | 'dark';

/**
 * Tell native how to draw the status bar over the screen we are showing.
 *
 * The style names the GLYPHS, not the background: 'light' means white
 * glyphs, which is what a dark screen needs.
 *
 * Only native can set this — iOS gives a WKWebView no say in it, and
 * `<meta name="theme-color">` does nothing there. But native cannot see
 * which screen it is showing, so without this the shell holds one style
 * for the whole app and it is wrong on half the screens: white glyphs
 * over Profile and Home were invisible against their light backgrounds.
 *
 * Fire-and-forget, and safe to call outside the WebView — the browser
 * has its own status bar and does not need ours.
 */
export function setNativeStatusBarStyle(style: NativeStatusBarStyle): void {
  if (!isNativeWebView()) return;
  postNativeMessage({ type: 'set_status_bar_style', payload: { style } });
}

/**
 * Send login message to React Native for push notification registration
 * @param token - JWT access token
 * @param member - Member object with id and deviceIds
 */
export function sendLoginMessage(token: string, member: { id: string; deviceIds?: string[] }): void {
  console.log('[NativeBridge] Sending login message for push notification registration');
  postNativeMessage({
    type: 'login',
    payload: {
      token,
      member: {
        id: member.id,
        deviceIds: member.deviceIds || []
      }
    }
  });
}

/**
 * Send logout message to React Native
 */
export function sendLogoutMessage(): void {
  console.log('[NativeBridge] Sending logout message');
  postNativeMessage({ type: 'logout' });
}

/**
 * Ask native to open a URL outside the app's own WebView — an in-app browser
 * (SFSafariViewController / Chrome Custom Tabs) rather than a new window.
 *
 * `target="_blank"` and `window.open` do nothing inside a WebView unless the
 * native shell has opted into multiple windows, so a plain link is not an
 * escape hatch there. This is: the payment page is the one place a member must
 * always be able to reach, even if it refuses to be framed.
 */
export function triggerNativeOpenUrl(url: string): void {
  console.log('[NativeBridge] Requesting native open-url for', url);
  postNativeMessage({ type: 'open_url', payload: { url } });
}

/**
 * Ask native whether this build is in sync (i.e. supports the calendar-sync
 * bridge messages). This is request/response rather than a native-initiated
 * broadcast on purpose — firing this only once our own message listener
 * (VersionSyncContext) is mounted avoids the race where native posts
 * `version_sync_status` before the WebView's JS has finished booting and
 * the message is lost with no listener to catch it.
 */
export function requestVersionSyncStatus(): void {
  console.log('[NativeBridge] Requesting version sync status');
  postNativeMessage({ type: 'request_version_sync_status' });
}

/**
 * Ask native to add a booking to the device's calendar (EventKit on iOS /
 * CalendarContract on Android). We hand over plain event data — never a
 * blob:/data: URL — because those only exist inside the WebView's JS
 * context and can't be resolved by native's Linking.openURL.
 */
export function triggerNativeAddToCalendar(event: CalendarEventInput): void {
  console.log('[NativeBridge] Requesting native add-to-calendar for', event.uid);
  postNativeMessage({
    type: 'add_to_calendar',
    payload: {
      title: event.title,
      description: event.description,
      location: event.location,
      startTime: event.startTime,
      endTime: event.endTime,
      uid: event.uid,
      // Minutes before startTime to fire a reminder alarm on the created
      // calendar event — matches the app's "2 hour" booking reminder push.
      alertMinutesBefore: event.alertMinutesBefore ?? CALENDAR_ALERT_MINUTES_BEFORE,
    },
  });
}

/**
 * Ask native to delete the calendar event for a cancelled booking, if one
 * was ever created (the user may never have tapped "Add to Calendar" for
 * this booking). Native owns the uid → calendar-event-identifier mapping
 * and is expected to no-op gracefully when it has no matching event —
 * the web side always fires this on cancel regardless of whether it knows
 * a calendar event exists.
 */
export function triggerNativeRemoveFromCalendar(uid: string): void {
  console.log('[NativeBridge] Requesting native remove-from-calendar for', uid);
  postNativeMessage({
    type: 'remove_from_calendar',
    payload: { uid },
  });
}

/**
 * Ask native to move an existing calendar event to a booking's new
 * date/time after a reschedule. Same uid as the original add-to-calendar
 * request (bookings are edited in place, not recreated, so the id — and
 * therefore the uid — stays the same). Native should no-op if it has no
 * event for this uid (the user never added it in the first place) rather
 * than creating a new one here.
 */
export function triggerNativeUpdateCalendarEvent(event: CalendarEventInput): void {
  console.log('[NativeBridge] Requesting native update-calendar-event for', event.uid);
  postNativeMessage({
    type: 'update_calendar_event',
    payload: {
      title: event.title,
      description: event.description,
      location: event.location,
      startTime: event.startTime,
      endTime: event.endTime,
      uid: event.uid,
      alertMinutesBefore: event.alertMinutesBefore ?? CALENDAR_ALERT_MINUTES_BEFORE,
    },
  });
}

/** How the native share sheet answered. `dismissed` is not an error. */
export interface NativeShareResult {
  success: boolean;
  dismissed?: boolean;
  error?: string;
}

/**
 * Hand a link to the OS share sheet, and resolve with what the member did.
 *
 * `navigator.share` is inert inside WKWebView unless the host app opts in, so
 * on iOS the web API silently is not there and the caller falls through to a
 * clipboard copy — which works, but loses the one interaction sharing a
 * connect link is for: picking the person out of Messages. Native has the
 * real sheet; this is the round trip to it.
 *
 * Request/response over one-way postMessage, so it listens for the reply and
 * gives up after a few seconds rather than leaving a promise pending forever
 * on a build whose bridge does not know this message.
 */
export function shareViaNative(payload: {
  url: string;
  message?: string;
  title?: string;
}): Promise<NativeShareResult> {
  if (!isNativeWebView()) {
    return Promise.resolve({ success: false, error: 'Not in a native shell' });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: NativeShareResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage as EventListener);
      window.clearTimeout(timer);
      resolve(result);
    };

    const onMessage = (event: MessageEvent | Event) => {
      try {
        const data = (event as MessageEvent).data;
        if (typeof data !== 'string') return;
        const parsed = JSON.parse(data);
        if (parsed?.type !== 'share_link_result') return;
        finish(parsed.payload ?? { success: false });
      } catch {
        /* Not ours — the WebView carries other traffic on this channel. */
      }
    };

    /* Android delivers to `document`, iOS to `window`. Both are listened for
       everywhere rather than branching on platform, which is what the rest of
       the app's message handling does. */
    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage as EventListener);

    const timer = window.setTimeout(
      () => finish({ success: false, error: 'No response from the app' }),
      8000,
    );

    postNativeMessage({ type: 'share_link', payload });
  });
}

/** What native's scanner sends back. `cancelled` means the member closed it. */
export interface NativeQrScanResult {
  success: boolean;
  value?: string;
  cancelled?: boolean;
  error?: string;
}

/**
 * Open the native QR scanner and wait for what it reads.
 *
 * Native because the in-WebView viewfinder needs `BarcodeDetector`, which is
 * Chromium-only — iOS never had a scanner at all. Resolves `success: false` on
 * a build whose bridge predates this message, so the caller can fall back to
 * the web screen.
 */
export function openNativeQrScanner(): Promise<NativeQrScanResult> {
  if (!isNativeWebView()) {
    return Promise.resolve({ success: false, error: 'Not in a native shell' });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: NativeQrScanResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage as EventListener);
      window.clearTimeout(timer);
      resolve(result);
    };

    const onMessage = (event: MessageEvent | Event) => {
      try {
        const data = (event as MessageEvent).data;
        if (typeof data !== 'string') return;
        const parsed = JSON.parse(data);
        if (parsed?.type !== 'open_qr_scanner_result') return;
        finish(parsed.payload ?? { success: false });
      } catch {
        /* Not ours — the WebView carries other traffic on this channel. */
      }
    };

    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage as EventListener);

    /* Long, unlike the share sheet's: the member is aiming a camera at
       somebody else's phone, and that takes as long as it takes. This only
       has to outlive an older build that will never answer. */
    const timer = window.setTimeout(
      () => finish({ success: false, error: 'No response from the app' }),
      120000,
    );

    postNativeMessage({ type: 'open_qr_scanner' });
  });
}
